import React, { useRef } from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Home image/feature section — clean rewrite of legacy molecules/HomeImageSection
// (Strapi sections.new-home-image-section + components.image-cards). Cards render
// as a horizontal carousel (3 up on desktop) with prev/next arrows, matching the
// live site. SSR-safe: native scroll-snap track + arrows that scrollBy one card
// (no carousel dependency / styled-system / next/image).
export const homeImageSectionSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  description: z.string().optional(),
  imageUrl: z.string().optional(),
  cards: z
    .array(
      z.object({
        imageUrl: z.string().optional(),
        heading: z.string().min(1),
        description: z.string().optional(),
      }),
    )
    .min(1)
    .max(12),
});

type HomeImageSectionProps = z.infer<typeof homeImageSectionSchema>;

const Section = styled.section`
  max-width: 144rem;
  margin: 0 auto;
  padding: 6rem 1.6rem;

  @media (min-width: 64em) {
    padding: 10rem 16rem;
  }
`;

const Header = styled.div`
  max-width: 72rem;
  margin: 0 auto 4rem;
  text-align: center;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 3px;
  text-transform: uppercase;
`;

const Heading = styled.h2`
  margin: 0.8rem 0 0;
  color: #0b1020;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: clamp(2.8rem, 4vw, 4.8rem);
`;

const Description = styled.p`
  margin: 0.8rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  line-height: 1.7;
`;

const Banner = styled.img`
  display: block;
  width: 100%;
  height: auto;
  max-width: 120rem;
  margin: 0 auto 5rem;
`;

const Carousel = styled.div`
  display: flex;
  align-items: stretch;
  gap: 1.2rem;

  @media (min-width: 64em) {
    gap: 2rem;
  }
`;

const Arrow = styled.button`
  appearance: none;
  flex: 0 0 auto;
  align-self: center;
  width: 4.8rem;
  height: 4.8rem;
  border: 1px solid #cdd9f5;
  border-radius: 50%;
  background: #ffffff;
  color: #063091;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #063091;
    color: #ffffff;
  }

  svg {
    width: 2rem;
    height: 2rem;
  }
`;

const Viewport = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  gap: 2.4rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  padding-bottom: 0.4rem;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Card = styled.div`
  flex: 0 0 85%;
  scroll-snap-align: start;
  box-sizing: border-box;
  padding: 2.8rem;
  border: 1px solid #e8efff;
  border-radius: 2rem;
  background: #ffffff;

  @media (min-width: 48em) {
    flex-basis: calc((100% - 2.4rem) / 2);
  }
  @media (min-width: 64em) {
    flex-basis: calc((100% - 4.8rem) / 3);
  }
`;

const CardImage = styled.img`
  width: 4.8rem;
  height: 4.8rem;
  object-fit: contain;
  margin-bottom: 1.6rem;
`;

const CardHeading = styled.h3`
  margin: 0;
  color: #0b1020;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 2rem;
`;

const CardDesc = styled.p`
  margin: 0.8rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  line-height: 1.6;
`;

const ChevronLeft = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronRight = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function HomeImageSection({
  eyebrow,
  heading,
  description,
  imageUrl,
  cards,
}: HomeImageSectionProps) {
  const viewport = useRef<HTMLDivElement>(null);
  // Scroll by exactly one card stride (measured from the first two cards, so it
  // stays correct across breakpoints); falls back to a viewport page.
  const scroll = (dir: 1 | -1) => {
    const vp = viewport.current;
    if (!vp) return;
    const items = vp.querySelectorAll<HTMLElement>('[data-card]');
    const stride = items.length > 1
      ? items[1]!.getBoundingClientRect().left - items[0]!.getBoundingClientRect().left
      : vp.clientWidth;
    vp.scrollBy({ left: dir * stride, behavior: 'smooth' });
  };
  const multi = cards.length > 1;

  return (
    <Section>
      <Header>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <Heading>{heading}</Heading>
        {description ? <Description>{description}</Description> : null}
      </Header>
      {/* CMS image if set; otherwise the legacy hard-coded platform diagram
          (a static asset served by apps/web at /images/LandingImage.svg). */}
      <Banner src={imageUrl || '/images/LandingImage.svg'} alt={heading} />
      <Carousel>
        {multi ? (
          <Arrow type="button" onClick={() => scroll(-1)} aria-label="Previous">
            {ChevronLeft}
          </Arrow>
        ) : null}
        <Viewport ref={viewport}>
          {cards.map((card, i) => (
            <Card key={`${card.heading}-${i}`} data-card>
              {card.imageUrl ? <CardImage src={card.imageUrl} alt={card.heading} /> : null}
              <CardHeading>{card.heading}</CardHeading>
              {card.description ? <CardDesc>{card.description}</CardDesc> : null}
            </Card>
          ))}
        </Viewport>
        {multi ? (
          <Arrow type="button" onClick={() => scroll(1)} aria-label="Next">
            {ChevronRight}
          </Arrow>
        ) : null}
      </Carousel>
    </Section>
  );
}

export const homeImageSectionBlock: BlockDefinition<HomeImageSectionProps> = {
  type: 'homeImageSection',
  schema: homeImageSectionSchema,
  Renderer: HomeImageSection,
};
